import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useProjectComments, useCreateProjectComment, useProjectActivity } from '@/hooks/use-comments';
import { useAuthStore } from '@/stores/auth-store';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import {
  Loader2,
  MessageSquare,
  History,
  Send,
  UserPlus,
  ArrowRight,
  CheckCircle2,
  FileText,
  Paperclip,
  Reply,
} from 'lucide-react';
import { formatDistanceToNow } from '@/lib/utils';

interface ProjectActivityProps {
  projectId: string;
}

const actionIcons: Record<string, React.ElementType> = {
  'auth.login': UserPlus,
  'auth.password_reset': History,
  'auth.password_change': CheckCircle2,
  'project.create': FileText,
  'project.update': ArrowRight,
  'project.delete': History,
  'task.create': FileText,
  'task.update': ArrowRight,
  'task.delete': History,
};

const actionLabels: Record<string, string> = {
  'auth.login': 'logged in',
  'auth.password_reset': 'reset password',
  'auth.password_change': 'changed password',
  'project.create': 'created the project',
  'project.update': 'updated the project',
  'project.delete': 'deleted the project',
  'task.create': 'created a task',
  'task.update': 'updated a task',
  'task.delete': 'deleted a task',
  'file.upload': 'uploaded a file',
  'ticket.create': 'created a ticket',
};

function highlightMentions(text: string): React.ReactNode {
  const parts = text.split(/(@[\w.]+)/g);
  return parts.map((part, i) =>
    part.startsWith('@') ? (
      <span key={i} className="text-dyndev-green font-medium">
        {part}
      </span>
    ) : (
      part
    )
  );
}

function CommentItem({
  comment,
  projectId,
  onReply,
}: {
  comment: any;
  projectId: string;
  onReply: (parentId: string, userName: string) => void;
}) {
  return (
    <div className="flex gap-3 p-4">
      <Avatar className="w-8 h-8 flex-shrink-0">
        <AvatarFallback className="text-xs font-bold bg-dyndev-green/20 text-dyndev-green">
          {comment.user?.name?.charAt(0)?.toUpperCase() || '?'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium">{comment.user?.name || 'Unknown'}</span>
          <span className="text-[10px] text-[var(--text-secondary)]">
            {formatDistanceToNow(comment.createdAt)}
          </span>
        </div>
        <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap">
          {highlightMentions(comment.content)}
        </p>
        <button
          type="button"
          onClick={() => onReply(comment.id, comment.user?.name || 'User')}
          className="mt-1 text-[10px] text-[var(--text-secondary)] hover:text-dyndev-green transition-colors flex items-center gap-1"
        >
          <Reply size={10} />
          Reply
        </button>
      </div>
    </div>
  );
}

export function ProjectActivity({ projectId }: ProjectActivityProps) {
  const { user } = useAuthStore();
  const [newComment, setNewComment] = useState('');
  const [showActivity, setShowActivity] = useState(false);
  const [replyTo, setReplyTo] = useState<{ id: string; name: string } | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: commentsData, isLoading: commentsLoading } = useProjectComments(projectId);
  const { data: activityData, isLoading: activityLoading } = useProjectActivity(projectId);
  const createComment = useCreateProjectComment();

  const comments = commentsData?.data || [];
  const activity = activityData?.data || [];

  // Group comments: parent comments (no parentId) and their replies
  const parentComments = comments.filter((c: any) => !c.parentId);
  const replyMap = new Map<string, any[]>();
  comments.forEach((c: any) => {
    if (c.parentId) {
      const existing = replyMap.get(c.parentId) || [];
      existing.push(c);
      replyMap.set(c.parentId, existing);
    }
  });

  const handleReply = (parentId: string, userName: string) => {
    setReplyTo({ id: parentId, name: userName });
    setShowActivity(false);
    textareaRef.current?.focus();
  };

  const handleCancelReply = () => {
    setReplyTo(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setUploadedFiles(Array.from(files));
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      // Upload files first if any
      let fileUrls: string[] = [];
      if (uploadedFiles.length > 0) {
        setUploading(true);
        const formData = new FormData();
        formData.append('projectId', projectId);
        uploadedFiles.forEach((f) => formData.append('files', f));
        const uploadRes = await apiClient.upload('/files', formData);
        if (uploadRes.success && uploadRes.data) {
          fileUrls = uploadRes.data.map((f: any) => f.originalName);
        }
        setUploading(false);
      }

      // Build comment content with file references
      let content = newComment.trim();
      if (fileUrls.length > 0) {
        content += '\n\n' + fileUrls.map((name) => `📎 ${name}`).join('\n');
      }

      await createComment.mutateAsync({
        projectId,
        content,
        parentId: replyTo?.id || undefined,
      });

      toast({
        title: 'Comment added',
        description: replyTo ? 'Your reply has been posted.' : 'Your comment has been posted.',
        variant: 'success',
      });

      setNewComment('');
      setReplyTo(null);
      setUploadedFiles([]);
    } catch (err: any) {
      toast({
        title: 'Failed to add comment',
        description: err?.message || 'An unexpected error occurred.',
        variant: 'destructive',
      });
    }
  };

  const actionIcon = (action: string): React.ElementType => {
    return actionIcons[action] || History;
  };

  const actionLabel = (action: string): string => {
    return actionLabels[action] || action?.replace(/_/g, ' ') || 'performed an action';
  };

  return (
    <div className="space-y-6">
      {/* Comment form */}
      <Card className={cn(replyTo && 'border-dyndev-green/30')}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">
              {replyTo ? `Reply to ${replyTo.name}` : 'Add a Comment'}
            </CardTitle>
            {replyTo && (
              <button
                type="button"
                onClick={handleCancelReply}
                className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              >
                Cancel reply
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmitComment} className="space-y-3">
            <Textarea
              ref={textareaRef}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={
                replyTo
                  ? `Reply to ${replyTo.name}... Use @name to mention someone.`
                  : 'Write a comment... Use @name to mention someone.'
              }
              rows={3}
            />

            {/* Uploaded files preview */}
            {uploadedFiles.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {uploadedFiles.map((file, i) => (
                  <span
                    key={i}
                    className="text-[11px] px-2 py-1 rounded bg-[var(--bg-secondary)] border border-[var(--border-color)] flex items-center gap-1"
                  >
                    <Paperclip size={10} />
                    {file.name}
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(i)}
                      className="text-[var(--text-secondary)] hover:text-red-400 ml-1"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="comment-file-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-1 text-[var(--text-secondary)] hover:text-dyndev-green"
                >
                  <Paperclip size={12} />
                  Attach files
                </Button>
                <p className="text-[10px] text-[var(--text-secondary)]">
                  Tip: Type <span className="text-dyndev-green">@name</span> to mention
                </p>
              </div>
              <Button
                type="submit"
                size="sm"
                disabled={createComment.isPending || uploading || !newComment.trim()}
                className="gap-1.5"
              >
                {createComment.isPending || uploading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                {replyTo ? 'Reply' : 'Post'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Toggle: Comments vs Activity */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowActivity(false)}
          className={cn(
            'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
            !showActivity
              ? 'bg-dyndev-green/10 text-dyndev-green border-dyndev-green/30'
              : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-dyndev-green/20'
          )}
        >
          <MessageSquare size={12} className="inline mr-1" />
          Comments ({comments.length})
        </button>
        <button
          type="button"
          onClick={() => setShowActivity(true)}
          className={cn(
            'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
            showActivity
              ? 'bg-dyndev-green/10 text-dyndev-green border-dyndev-green/30'
              : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-dyndev-green/20'
          )}
        >
          <History size={12} className="inline mr-1" />
          Activity ({activity.length})
        </button>
      </div>

      {/* Comments view */}
      {!showActivity && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[var(--text-secondary)]">
              Recent Comments
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {commentsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-dyndev-green" />
              </div>
            ) : parentComments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <MessageSquare size={32} className="text-[var(--text-secondary)] mb-3" />
                <p className="text-sm font-medium mb-1">No comments yet</p>
                <p className="text-xs text-[var(--text-secondary)]">
                  Be the first to comment on this project.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--border-color)]">
                {parentComments.map((comment: any) => (
                  <div key={comment.id}>
                    <CommentItem comment={comment} projectId={projectId} onReply={handleReply} />
                    {/* Threaded replies */}
                    {replyMap.get(comment.id)?.map((reply: any) => (
                      <div key={reply.id} className="ml-12 border-l-2 border-[var(--border-color)] pl-4">
                        <CommentItem comment={reply} projectId={projectId} onReply={handleReply} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Activity view */}
      {showActivity && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-[var(--text-secondary)]">
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {activityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-dyndev-green" />
              </div>
            ) : activity.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <History size={32} className="text-[var(--text-secondary)] mb-3" />
                <p className="text-sm font-medium mb-1">No activity yet</p>
                <p className="text-xs text-[var(--text-secondary)]">
                  Project activity will appear here as changes are made.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--border-color)]">
                {activity.map((entry: any) => {
                  const Icon = entry.type === 'comment'
                    ? MessageSquare
                    : actionIcon(entry.data?.action);

                  return (
                    <div key={entry.id} className="flex gap-3 p-4">
                      {entry.type === 'comment' ? (
                        <Avatar className="w-8 h-8 flex-shrink-0">
                          <AvatarFallback className="text-xs font-bold bg-dyndev-green/20 text-dyndev-green">
                            {entry.user?.name?.charAt(0)?.toUpperCase() || '?'}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                          <Icon size={14} className="text-blue-400" />
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {entry.type === 'comment' && (
                            <MessageSquare size={12} className="text-dyndev-green" />
                          )}
                          <span className="text-sm font-medium">
                            {entry.user?.name || 'System'}
                          </span>
                          <span className="text-[10px] text-[var(--text-secondary)]">
                            {entry.type === 'comment' ? 'commented ' : ''}
                            {formatDistanceToNow(entry.createdAt)}
                          </span>
                        </div>
                        {entry.type === 'comment' ? (
                          <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap line-clamp-2">
                            {highlightMentions(entry.data?.content || '')}
                          </p>
                        ) : (
                          <p className="text-sm text-[var(--text-secondary)]">
                            {actionLabel(entry.data?.action)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
